// Device helpers

/**
 * Detects if the current device is iOS
 */
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false

  const userAgent = window.navigator.userAgent.toLowerCase()
  const platform = (window.navigator as any).userAgentData?.platform || window.navigator.platform

  // Check for iOS devices
  return (
    /iphone|ipad|ipod/.test(userAgent) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  ) // iPad on iOS 13+
}

/**
 * Detects if the current device is Android
 */
export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false

  const userAgent = window.navigator.userAgent.toLowerCase()
  return /android/.test(userAgent)
}

/**
 * Detects if the current device is mobile (iOS or Android)
 */
export const isMobile = (): boolean => {
  return isIOS() || isAndroid()
}

/**
 * Gets the browser name
 */
const getBrowserName = (): string => {
  if (typeof window === 'undefined') return 'unknown'

  const userAgent = navigator.userAgent

  if (userAgent.includes('Firefox/')) return 'Firefox'
  if (userAgent.includes('Edg/')) return 'Edge'
  if (userAgent.includes('Chrome/')) return 'Chrome'
  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return 'Safari'
  if (userAgent.includes('Opera/') || userAgent.includes('OPR/')) return 'Opera'
  if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) return 'Internet Explorer'

  return 'unknown'
}

/**
 * Gets the browser version
 */
const getBrowserVersion = (): string => {
  if (typeof window === 'undefined') return 'unknown'

  const userAgent = navigator.userAgent
  let match: RegExpMatchArray | null

  match = userAgent.match(/Firefox\/([0-9.]+)/)
  if (match) return match[1]

  match = userAgent.match(/Edg\/([0-9.]+)/)
  if (match) return match[1]

  match = userAgent.match(/Chrome\/([0-9.]+)/)
  if (match) return match[1]

  match = userAgent.match(/Version\/([0-9.]+)/)
  if (match) return match[1]

  match = userAgent.match(/Opera\/([0-9.]+)/)
  if (match) return match[1]

  match = userAgent.match(/OPR\/([0-9.]+)/)
  if (match) return match[1]

  return 'unknown'
}

/**
 * Gets the operating system name and version
 */
const getOS = (): { name: string; version: string } => {
  if (typeof window === 'undefined') return { name: 'unknown', version: 'unknown' }

  const userAgent = navigator.userAgent
  let name = 'unknown'
  let version = 'unknown'
  let match: RegExpMatchArray | null

  if (isIOS()) {
    name = 'iOS'
    match = userAgent.match(/OS (\d+)[_.](\d+)[_.]?(\d+)?/)
    if (match) {
      version = match[1] + '.' + match[2] + (match[3] ? '.' + match[3] : '')
    }
  } else if (isAndroid()) {
    name = 'Android'
    match = userAgent.match(/Android (\d+(?:\.\d+)*)/)
    if (match) {
      version = match[1]
    }
  } else if (userAgent.includes('Windows NT')) {
    name = 'Windows'
    match = userAgent.match(/Windows NT (\d+\.\d+)/)
    if (match) {
      const ntVersion = match[1]
      const versionMap: Record<string, string> = {
        '10.0': '10/11',
        '6.3': '8.1',
        '6.2': '8',
        '6.1': '7',
        '6.0': 'Vista',
      }
      version = versionMap[ntVersion] || ntVersion
    }
  } else if (userAgent.includes('Mac OS X')) {
    name = 'macOS'
    match = userAgent.match(/Mac OS X (\d+)[_.](\d+)[_.]?(\d+)?/)
    if (match) {
      version = match[1] + '.' + match[2] + (match[3] ? '.' + match[3] : '')
    }
  } else if (userAgent.includes('Linux')) {
    name = 'Linux'
  } else if (userAgent.includes('CrOS')) {
    name = 'Chrome OS'
  }

  return { name, version }
}

/**
 * Gets the device type
 */
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  if (typeof window === 'undefined') return 'desktop'

  const userAgent = navigator.userAgent.toLowerCase()

  // Check for tablet
  if (/ipad|android(?!.*mobile)|tablet/.test(userAgent)) {
    return 'tablet'
  }

  // Check for mobile
  if (/mobile|iphone|ipod|android/.test(userAgent)) {
    return 'mobile'
  }

  return 'desktop'
}

/**
 * Gets network connection information
 */
const getConnectionInfo = (): {
  effectiveType?: string
  downlink?: number
  rtt?: number
  saveData?: boolean
} => {
  if (typeof window === 'undefined' || !('connection' in navigator)) {
    return {}
  }

  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection

  if (!connection) return {}

  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
  }
}

/** Compact device profile fields for API upserts (alpine.devices). */
export const getDeviceProfileFields = (): {
  deviceClass: 'mobile' | 'tablet' | 'desktop'
  os: string
  browser: string
  userAgent: string
} => {
  if (typeof window === 'undefined') {
    return { deviceClass: 'desktop', os: '', browser: '', userAgent: '' }
  }
  return {
    deviceClass: getDeviceType(),
    os: getOS().name,
    browser: getBrowserName(),
    userAgent: navigator.userAgent || '',
  }
}

/**
 * Gets a unique device ID that persists in local storage
 */
export const getDeviceId = (): string => {
  if (typeof window === 'undefined') return 'unknown'

  const STORAGE_KEY = 'aiq_device_id'
  let deviceId = localStorage.getItem(STORAGE_KEY)

  if (!deviceId) {
    // Generate a UUID-like string
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      deviceId = crypto.randomUUID()
    } else {
      // Fallback for older browsers
      deviceId = `dev-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
    }
    localStorage.setItem(STORAGE_KEY, deviceId)
  }

  return deviceId
}

/**
 * Generates a device fingerprint hash based on stable device characteristics.
 * This provides a best-effort unique ID that persists even if local storage is cleared.
 * Note: This is not 100% unique (collisions possible on identical devices) but consistent.
 */
export const getDeviceFingerprint = (): string => {
  if (typeof window === 'undefined') return 'unknown'

  const { screen, navigator } = window

  // Collect stable characteristics
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.hardwareConcurrency,
    (navigator as any).deviceMemory,
    navigator.platform,
  ]

  // Create a simple hash from the components
  const fingerprintString = components.join('|')
  let hash = 0
  for (let i = 0; i < fingerprintString.length; i++) {
    const char = fingerprintString.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }

  // Return as absolute hex string
  return Math.abs(hash).toString(16)
}

/**
 * Device information interface
 */
export interface DeviceInfo {
  // User Agent
  userAgent: string

  // Browser Information
  browser: {
    name: string
    version: string
  }

  // Operating System
  os: {
    name: string
    version: string
  }

  // Device Information
  device: {
    id: string
    fingerprint: string
    type: 'mobile' | 'tablet' | 'desktop'
    vendor: string
    model: string
    isIOS: boolean
    isAndroid: boolean
    isMobile: boolean
    isTablet: boolean
    isDesktop: boolean
    isTouchDevice: boolean
  }

  // Screen Information
  screen: {
    width: number
    height: number
    availWidth: number
    availHeight: number
    colorDepth: number
    pixelDepth: number
    orientation?: string
    pixelRatio: number
  }

  // Viewport Information
  viewport: {
    width: number
    height: number
  }

  // Language & Locale
  language: string
  languages: readonly string[]

  // Timezone
  timezone: string
  timezoneOffset: number

  // Platform
  platform: string

  // Hardware
  hardware: {
    cpuCores: number
    deviceMemory?: number // in GB
    maxTouchPoints: number
  }

  // Network
  connection?: {
    effectiveType?: string
    downlink?: number
    rtt?: number
    saveData?: boolean
  }

  // Features
  features: {
    cookieEnabled: boolean
    doNotTrack: string | null
    pdfViewerEnabled: boolean
    webdriver: boolean
    onLine: boolean
  }

  // Timestamp
  timestamp: string
  timestampISO: string
}

/**
 * Collects comprehensive device information for legal compliance tracking
 * Use this during sign-up to capture all identifiable information
 */
export const getDeviceInfo = (): DeviceInfo => {
  if (typeof window === 'undefined') {
    throw new Error('getDeviceInfo can only be called in a browser environment')
  }

  const os = getOS()
  const deviceType = getDeviceType()
  const now = new Date()

  return {
    userAgent: navigator.userAgent,

    browser: {
      name: getBrowserName(),
      version: getBrowserVersion(),
    },

    os,

    device: {
      id: getDeviceId(),
      fingerprint: getDeviceFingerprint(),
      type: deviceType,
      vendor: navigator.vendor || 'unknown',
      model: (navigator as any).userAgentData?.model || 'unknown',
      isIOS: isIOS(),
      isAndroid: isAndroid(),
      isMobile: deviceType === 'mobile',
      isTablet: deviceType === 'tablet',
      isDesktop: deviceType === 'desktop',
      isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    },

    screen: {
      width: window.screen.width,
      height: window.screen.height,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
      orientation: window.screen.orientation?.type || 'unknown',
      pixelRatio: window.devicePixelRatio || 1,
    },

    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },

    language: navigator.language,
    languages: navigator.languages,

    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: now.getTimezoneOffset(),

    platform: navigator.platform,

    hardware: {
      cpuCores: navigator.hardwareConcurrency || 0,
      deviceMemory: (navigator as any).deviceMemory,
      maxTouchPoints: navigator.maxTouchPoints,
    },

    connection: getConnectionInfo(),

    features: {
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: (navigator as any).doNotTrack || null,
      pdfViewerEnabled: (navigator as any).pdfViewerEnabled || false,
      webdriver: (navigator as any).webdriver || false,
      onLine: navigator.onLine,
    },

    timestamp: now.toISOString(),
    timestampISO: now.toISOString(),
  }
}
